import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { InspectModeRepository } from '../state/inspect-mode-repository.service';
import {
  GALLERY_VIEW_RELATED_VIEWS,
  SessionSharedDataService,
} from './session-shared-data.service';
import { ProviderStateService } from './provider-state.service';
import { ChildModeService } from './child-mode.service';

export enum SessionOverlayModalType {
  GV_CALL_NOT_CONNECTED_OVERLAY_MODAL = 'gvCallNotConnectedOverlayModal',
}

export interface OverlayModalShownActions {
  backToBoards: boolean;
  chat: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SessionOverlayModalsService {
  private _gvCallNotConnectedOverlayModalShown$ = new BehaviorSubject<boolean>(false);
  public gvCallNotConnectedOverlayModalShown$ =
    this._gvCallNotConnectedOverlayModalShown$.asObservable();

  public showOverLayModal$ = this.gvCallNotConnectedOverlayModalShown$;

  private _currentOverLayModal$ = new BehaviorSubject<SessionOverlayModalType | undefined>(
    undefined,
  );
  public currentOverLayModal$ = this._currentOverLayModal$.asObservable();

  readonly defaultShownActions: OverlayModalShownActions = {
    backToBoards: false,
    chat: false,
  };
  readonly showAllActions: OverlayModalShownActions = {
    backToBoards: true,
    chat: true,
  };
  private _overlayModalBottomBarShownActions$ = new BehaviorSubject<OverlayModalShownActions>(
    this.defaultShownActions,
  );
  public overlayModalBottomBarShownActions$ =
    this._overlayModalBottomBarShownActions$.asObservable();

  private _overlayModalBottomBarWidth$ = new BehaviorSubject<number>(0);
  public overlayModalBottomBarWidth$ = this._overlayModalBottomBarWidth$.asObservable();

  constructor(
    private readonly sessionSharedDataService: SessionSharedDataService,
    private readonly providerStateService: ProviderStateService,
    private readonly inspectModeRepository: InspectModeRepository,
    private readonly childModeService: ChildModeService,
  ) {
    combineLatest([
      this.gvCallNotConnectedOverlayModalShown$,
      this.childModeService.isChildModeActive$,
    ]).subscribe(([gvCallNotConnectedOverlayModalShown, isChildModeActive]) => {
      if (gvCallNotConnectedOverlayModalShown) {
        this.initGVCallNotConnectedOverlayModal(isChildModeActive);
        return;
      }

      this._currentOverLayModal$.next(undefined);
    });

    // GV call not connected overlay modal showing logic
    combineLatest([
      this.sessionSharedDataService.sessionView.current$,
      this.providerStateService.callConnected$,
    ]).subscribe(([sessionViewState, callConnected]) => {
      const currentSessionView = sessionViewState.view;
      const isGalleryViewRelatedView = GALLERY_VIEW_RELATED_VIEWS.includes(currentSessionView);

      if (!isGalleryViewRelatedView || callConnected || this.inspectModeRepository.isInspectMode) {
        this._gvCallNotConnectedOverlayModalShown$.next(false);
        return;
      }

      // call not connected and in gallery view
      this._gvCallNotConnectedOverlayModalShown$.next(true);
    });
  }

  initGVCallNotConnectedOverlayModal(isChildModeActive = false) {
    this._currentOverLayModal$.next(SessionOverlayModalType.GV_CALL_NOT_CONNECTED_OVERLAY_MODAL);
    this._overlayModalBottomBarShownActions$.next({
      backToBoards: true,
      chat: !isChildModeActive,
    });
  }

  removeOverlayModal() {
    this._gvCallNotConnectedOverlayModalShown$.next(false);
    this._currentOverLayModal$.next(undefined);
    this._overlayModalBottomBarShownActions$.next(this.defaultShownActions);
    this._overlayModalBottomBarWidth$.next(0);
  }

  updateOverlayModalBottomBarWidth(newWidth: number) {
    this._overlayModalBottomBarWidth$.next(newWidth);
  }

  isGVCallNotConnectedOverlayModalShown() {
    return this._gvCallNotConnectedOverlayModalShown$.getValue();
  }
}
